/**
 * Hook de gestion de l'édition d'un profil utilisateur.
 * Responsabilités :
 * - charger le profil cible
 * - exposer les tags administrables par l'utilisateur courant
 * - initialiser le formulaire
 * - soumettre les mises à jour des rôles et du statut super-admin
 */

import { skipToken } from '@reduxjs/toolkit/query'
import { toast } from 'core/components/common/Toast'
import {
  ROLE_ADMIN_VALUE,
  ROLE_DESACTIVE_VALUE,
  ROLE_USER_VALUE,
} from 'core/constants/role'
import { ROUTE_PATHS } from 'core/constants/routePath'
import { UserContext } from 'core/contexts/UserContext'
import { useMyPermissions } from 'core/hooks/useMyPermissions'
import type { IApiClientTagMe } from 'core/interfaces/Me'
import {
  useGetUtilisateurByClientQuery,
  useUpdateUtilisateurMutation,
  useUpdateUtilisateurTagsMutation,
} from 'lauria/api/utilisateurs'
import type { UtilisateurPermission } from 'lauria/types/utilisateur'
import { useCallback, useContext, useEffect, useMemo } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { generatePath, useNavigate } from 'react-router'
import { useParams } from 'react-router-dom'

export type ProfileForm = {
  superAdmin: boolean
  roles: { idTag: number; idRole: number }[]
}

export const useEditProfile = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { isSuperAdmin: isCurrentUserSuperadmin } = useMyPermissions()

  const { user: currentUser, tagsMe } = useContext(UserContext)

  /**
   * Si l'utilisateur connecté est superadmin, on retourne tous les tags
   * Sinon, on retourne les tags des administrateurs
   */
  const currentUserTagAdmin = useMemo((): IApiClientTagMe[] => {
    if (isCurrentUserSuperadmin) return tagsMe
    return tagsMe.filter((tagMe: IApiClientTagMe) => {
      return tagMe.permission === 'administrateur'
    })
  }, [isCurrentUserSuperadmin, tagsMe])

  const idClient = currentUser?.idClient

  const { idProfile } = useParams<{ idProfile: string }>()
  const idUtilisateur = useMemo((): number | undefined => {
    if (!idProfile) return undefined
    const parsed = Number(idProfile)
    return Number.isFinite(parsed) ? parsed : undefined
  }, [idProfile])

  const { data: user, isLoading: isLoadingUser } =
    useGetUtilisateurByClientQuery(
      idClient && idUtilisateur ? { idClient, idUtilisateur } : skipToken
    )

  /**
   * 2 mutations différentes :
   * - updateUtilisateur : pour mettre à jour le type de profil (Super-Admin ou non) de l'utilisateur
   * - updateUtilisateurTags : pour mettre à jour les tags de l'utilisateur
   */
  const [updateUtilisateur] = useUpdateUtilisateurMutation()
  const [updateUtilisateurTags] = useUpdateUtilisateurTagsMutation()

  const { handleSubmit, control, reset, register } = useForm<ProfileForm>({
    defaultValues: {
      superAdmin: false,
      roles: [],
    },
  })

  const { fields: roleFields, update: updateRole } = useFieldArray({
    control,
    name: 'roles',
  })

  const getUserRole = useCallback(
    (tagId: number): number => {
      const permission = user?.tags?.find((t) => t.idTag === tagId)?.permission

      if (permission === 'utilisateur') return ROLE_USER_VALUE
      if (permission === 'administrateur') return ROLE_ADMIN_VALUE
      return ROLE_DESACTIVE_VALUE
    },
    [user?.tags]
  )

  useEffect(() => {
    if (!user) return

    reset({
      superAdmin: Boolean(user.superAdmin),
      roles: currentUserTagAdmin.map((tag) => ({
        idTag: tag.id,
        idRole: getUserRole(tag.id),
      })),
    })
  }, [user, currentUserTagAdmin, getUserRole, reset])

  const roleIdToPermission = (roleId: number): UtilisateurPermission | null => {
    if (roleId === ROLE_USER_VALUE) return 'utilisateur'
    if (roleId === ROLE_ADMIN_VALUE) return 'administrateur'
    return null
  }

  /**
   * Gestion du formulaire :
   * - Si l'utilisateur connecté est superadmin et que le type de profil a changé, on le met à jour
   * - Si les tags ont changé, on met à jour les tags de l'utilisateur
   */
  const onSubmit = handleSubmit(async (data) => {
    if (!idClient || !idUtilisateur || !user) return

    const hasSuperAdminChanged =
      isCurrentUserSuperadmin && data.superAdmin !== Boolean(user.superAdmin)

    try {
      if (hasSuperAdminChanged) {
        await updateUtilisateur({
          idUtilisateur,
          body: {
            superAdmin: data.superAdmin,
          },
        }).unwrap()
      }

      await updateUtilisateurTags({
        idClient,
        idUtilisateur,
        body: {
          tags: data.roles
            .map((role) => {
              const permission = roleIdToPermission(role.idRole)
              return permission ? { idTag: role.idTag, permission } : null
            })
            .filter(
              (
                tag
              ): tag is { idTag: number; permission: UtilisateurPermission } =>
                tag !== null
            ),
        },
      }).unwrap()

      toast.success(t('profiles.updateSuccess'))
      navigate(generatePath(ROUTE_PATHS.PROFILES))
    } catch (error) {
      console.error(error)
    }
  })

  const cancelAction = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    navigate(generatePath(ROUTE_PATHS.PROFILES))
  }

  return {
    user,
    isCurrentUserSuperadmin,
    onSubmit,
    isLoading: isLoadingUser,
    currentUserTagAdmin,
    roleFields,
    updateRole,
    cancelAction,
    register,
  }
}
